import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { userService, type UpdateUserData } from "../services/userService";
import ProfileForm from "../components/layout/ProfileForm";
import {
  AlertDialog,
  type AlertDialogType,
} from "../components/ui/AlertDialog/AlertDialog";
import "./Settings.css";

const Settings = () => {
  const { user: authUser } = useAuth();
  const isInstructor = authUser?.role === "instructor";

  // Profile state
  const [profileData, setProfileData] = useState<UpdateUserData>({
    display_name: "",
    bio: "",
    phone: "",
    department: "",
    student_id: "",
  });
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  // Alert state
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertType, setAlertType] = useState<AlertDialogType>("info");
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");

  // Load user profile
  useEffect(() => {
    const loadProfile = async () => {
      if (!authUser?.id) return;

      try {
        setIsLoadingProfile(true);
        const userData = await userService.getUser(authUser.id);
        setProfileData({
          display_name: userData.display_name || "",
          bio: userData.bio || "",
          phone: userData.phone || "",
          department: userData.department || "",
          student_id: userData.student_id || "",
        });
      } catch (error) {
        console.error("Failed to load profile:", error);
        showAlert("error", "Error", "Failed to load profile data");
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadProfile();
  }, [authUser?.id]);

  const showAlert = (type: AlertDialogType, title: string, message: string) => {
    setAlertType(type);
    setAlertTitle(title);
    setAlertMessage(message);
    setIsAlertOpen(true);
  };

  const profileContent = isLoadingProfile ? (
    <div
      className={isInstructor ? "instructor-loading-state" : "text-center py-8"}
    >
      Loading profile...
    </div>
  ) : (
    <ProfileForm
      authUserId={authUser?.id || ""}
      authUserEmail={authUser?.email || ""}
      authUserRole={authUser?.role}
      initialData={profileData}
      onSuccess={(message) => showAlert("success", "Success", message)}
      onError={(message) => showAlert("error", "Error", message)}
    />
  );

  const alertDialog = (
    <AlertDialog
      isOpen={isAlertOpen}
      type={alertType}
      title={alertTitle}
      message={alertMessage}
      primaryButtonText="Close"
      onPrimaryAction={() => setIsAlertOpen(false)}
      onClose={() => setIsAlertOpen(false)}
      showCloseButton
    />
  );

  if (isInstructor) {
    return (
      <div className="instructor-page instructor-settings-page">
        <div className="instructor-top-bar">
          <div>
            <h1 className="instructor-page-title">Settings</h1>
            <div className="instructor-page-sub">
              Manage your instructor profile information
            </div>
          </div>
        </div>

        <section className="instructor-card instructor-settings-card">
          <div className="instructor-card-header">
            <div>
              <h2 className="instructor-card-title">Profile Information</h2>
              <div className="instructor-card-sub">
                Update how your name and contact details appear to students.
              </div>
            </div>
            <span className="instructor-badge">Instructor</span>
          </div>

          <div className="instructor-settings-form-shell">{profileContent}</div>
        </section>

        {alertDialog}
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 h-screen flex flex-col">
      <div className="mx-auto max-w-7xl w-full flex-1 min-h-0 flex flex-col">
        <div className="space-y-6 flex-1 min-h-0 flex flex-col">
          <div className="dashboard-card px-6 py-4 flex-shrink-0">
            <h5 className="heading-5">Settings</h5>
            <p className="subtitle-2 text-grey-80">
              Manage your profile information
            </p>
          </div>

          <div className="dashboard-card px-6 py-6 flex-1 min-h-0 overflow-y-auto">
            <div className="max-w-2xl">{profileContent}</div>
          </div>
        </div>
      </div>

      {alertDialog}
    </div>
  );
};

export default Settings;
